import { useEffect, useMemo, useState } from 'react';
import Drawer from '@app/components/Drawer';
import type { CloudAccessConnection, DiscoveryApiClass, DiscoveryJobschema, DiscoveryRun, Site, SitesApiClass } from '@app/types/sdk';
import { describeDiscoverySchedule } from './helpers';
import { resolveAzureRunReadiness } from './azureRunReadiness';

interface DiscoveryRunDrawerProps {
  open: boolean;
  orgId: string | null;
  discoveryApi: DiscoveryApiClass;
  sitesApi: SitesApiClass;
  jobs: DiscoveryJobschema[];
  jobId: string | null;
  siteNames: Record<string, string>;
  sites: Site[];
  onClose: () => void;
  onStarted: (run: DiscoveryRun) => void;
}

const DiscoveryRunDrawer = ({
  open,
  orgId,
  discoveryApi,
  sitesApi,
  jobs,
  jobId,
  siteNames,
  sites,
  onClose,
  onStarted
}: DiscoveryRunDrawerProps) => {
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [azureConnections, setAzureConnections] = useState<CloudAccessConnection[]>([]);
  const [azureConnectionsLoading, setAzureConnectionsLoading] = useState(false);
  const [azureConnectionsError, setAzureConnectionsError] = useState<string | null>(null);
  const [resolvedSite, setResolvedSite] = useState<Site | null>(null);
  const [selectedSiteLoading, setSelectedSiteLoading] = useState(false);
  const [selectedSiteError, setSelectedSiteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const fallback = jobs[0]?.id ?? '';
      setSelectedJobId(jobId ?? fallback ?? '');
      setError(null);
    } else {
      setSelectedJobId('');
      setSubmitting(false);
      setError(null);
    }
  }, [open, jobId, jobs]);

  const selectedJob = useMemo(() => jobs.find((job) => job.id === selectedJobId) ?? null, [jobs, selectedJobId]);
  const selectedSiteId = selectedJob?.siteId ?? null;
  useEffect(() => {
    if (!open || !orgId || !selectedSiteId) {
      setResolvedSite(null);
      setSelectedSiteLoading(false);
      setSelectedSiteError(null);
      return;
    }
    if (sites.some((site) => site.id === selectedSiteId)) {
      setResolvedSite(null);
      setSelectedSiteLoading(false);
      setSelectedSiteError(null);
      return;
    }

    const controller = new AbortController();
    let active = true;
    setResolvedSite(null);
    setSelectedSiteLoading(true);
    setSelectedSiteError(null);
    void sitesApi.orgsOrgIdSitesSiteIdGet(
      { orgId, siteId: selectedSiteId },
      { cache: 'no-store', signal: controller.signal }
    ).then(
      (site: Site) => {
        if (active) {
          setResolvedSite(site);
        }
      },
      () => {
        if (active && !controller.signal.aborted) {
          setSelectedSiteError('Unable to check the selected Site. Refresh and try again.');
        }
      }
    ).finally(() => {
      if (active) {
        setSelectedSiteLoading(false);
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [open, orgId, selectedSiteId, sites, sitesApi]);

  useEffect(() => {
    if (!open || !orgId) {
      setAzureConnections([]);
      setAzureConnectionsLoading(false);
      setAzureConnectionsError(null);
      return;
    }

    const controller = new AbortController();
    let active = true;
    const loadAzureConnections = async () => {
      setAzureConnections([]);
      setAzureConnectionsLoading(true);
      setAzureConnectionsError(null);
      try {
        const connections: CloudAccessConnection[] = [];
        const seenCursors = new Set<string>();
        let after: string | undefined;
        for (;;) {
          const response = await sitesApi.listProviderCloudAccessConnections(
            { orgId, provider: 'azure', limit: 200, after },
            { cache: 'no-store', signal: controller.signal }
          );
          connections.push(...(response.items ?? []));
          const next = typeof response.meta?.next === 'string' && response.meta.next.length > 0 ? response.meta.next : undefined;
          if (!next || seenCursors.has(next)) {
            break;
          }
          seenCursors.add(next);
          after = next;
        }
        if (active) {
          setAzureConnections(connections);
        }
      } catch {
        if (active && !controller.signal.aborted) {
          setAzureConnectionsError('Unable to check current Azure access. Refresh and try again.');
        }
      } finally {
        if (active) {
          setAzureConnectionsLoading(false);
        }
      }
    };
    void loadAzureConnections();
    return () => {
      active = false;
      controller.abort();
    };
  }, [open, orgId, sitesApi]);

  const readinessSites = useMemo(
    () => (resolvedSite ? [...sites.filter((site) => site.id !== resolvedSite.id), resolvedSite] : sites),
    [resolvedSite, sites]
  );
  const azureReadiness = useMemo(
    () => resolveAzureRunReadiness(selectedJob, readinessSites, azureConnections),
    [azureConnections, readinessSites, selectedJob]
  );
  const startBlockedMessage = selectedSiteLoading
    ? 'Checking the selected Site before starting Discovery…'
    : selectedSiteError
    ? selectedSiteError
    : azureConnectionsLoading
    ? 'Checking current Azure access…'
    : azureConnectionsError ?? azureReadiness.message;
  const startDisabled = submitting || selectedSiteLoading || Boolean(selectedSiteError) || (azureReadiness.isAzure && (azureConnectionsLoading || !azureReadiness.enabled || Boolean(azureConnectionsError)));
  const resolveJobLabel = (job: DiscoveryJobschema | null) => {
    if (!job) {
      return '—';
    }
    const jobWithName = job as DiscoveryJobschema & { name?: string; slug?: string };
    return jobWithName.name ?? jobWithName.slug ?? job.id ?? '—';
  };

  const jobLabel = resolveJobLabel(selectedJob);
  const siteLabel = useMemo(() => {
    if (!selectedJob?.siteId) {
      return 'Manual run';
    }
    return siteNames[selectedJob.siteId] ?? selectedJob.siteId;
  }, [selectedJob?.siteId, siteNames]);

  const formatSiteOption = (job: DiscoveryJobschema) => {
    const siteDisplay = (job.siteId ? siteNames[job.siteId] : undefined) ?? job.siteId ?? 'Unknown site';
    const jobDisplay = resolveJobLabel(job);
    return `${siteDisplay} • Job ${jobDisplay} • ${describeDiscoverySchedule(job.schedule)}`;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!orgId) {
      setError('Select an organization first.');
      return;
    }
    if (!selectedJob?.id) {
      setError('Choose a job to run.');
      return;
    }
    if (startDisabled) {
      setError(startBlockedMessage ?? 'Azure access must be verified before Discovery can run.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const run = await discoveryApi.orgsOrgIdDiscoveryJobsJobIdRunsPost({
        orgId,
        jobId: selectedJob.id
      });
      onStarted(run);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Failed to start a discovery run.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      title="Start discovery run"
      subtitle={siteLabel}
      onClose={() => {
        if (!submitting) {
          onClose();
        }
      }}
    >
      <form className="discovery-form" onSubmit={handleSubmit}>
        <label>
          Job
          <select value={selectedJobId} onChange={(event) => setSelectedJobId(event.target.value)}>
            <option value="">Select job</option>
            {jobs.map((job) => (
              <option key={job.id ?? job.siteId} value={job.id ?? ''}>
                {formatSiteOption(job)}
              </option>
            ))}
          </select>
        </label>

        {selectedJob ? (
          <div className="discovery-run-overview">
            <p>
              <strong>Job:</strong> {jobLabel}
            </p>
            <p>
              <strong>Site:</strong> {siteLabel}
            </p>
            <p>
              <strong>Seeds:</strong> {(selectedJob.seeds ?? []).join(', ') || 'None'}
            </p>
            <p>
              <strong>Targets:</strong> {(selectedJob.targetCidrs ?? []).join(', ') || 'Inherited from site'}
            </p>
            <p>
              <strong>Schedule:</strong> {describeDiscoverySchedule(selectedJob.schedule)}
            </p>
            {azureReadiness.isAzure && !azureReadiness.enabled ? (
              <p className="discovery-form__help">{startBlockedMessage ?? 'Azure is still applying access.'}</p>
            ) : null}
            <p className="discovery-form__help">Backend enforces one active run per job; queued requests wait for the current run to finish.</p>
          </div>
        ) : (
          <p className="discovery-form__help">Pick a job to see its scope before launching.</p>
        )}

        {error && <p className="discovery-form__error">{error}</p>}

        <div className="discovery-form__footer">
          <button type="button" className="ui-button ui-button--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="ui-button ui-button--primary" disabled={startDisabled} title={startDisabled ? startBlockedMessage : undefined}>
            {submitting ? 'Starting…' : 'Start Run'}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default DiscoveryRunDrawer;
